import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { ICON_SIZES, ICON_TYPES } from "@/constants/icons";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import {
  selectIsACPEligible,
  selectIsTeensAndKidsProgram,
} from "@/redux/selectors/patientSelectors";
import { logOut } from "@/redux/actions/auth";
import { shouldShowVulnerablePages } from "@/utils/authUtils";
import { usePopUpVisibility } from "@/utils/hooks";

import { AddUserIcon } from "../../../Icons/AddUserIcon";
import { AptihealthIcon } from "../../../Icons/AptihealthIcon";
import { BellIcon } from "../../../Icons/BellIcon";
import { CameraIcon } from "../../../Icons/CameraIcon";
import { CardIcon } from "../../../Icons/CardIcon";
import { ChevronDownIcon } from "../../../Icons/ChevronDownIcon";
import { DocumentIcon } from "../../../Icons/DocumentIcon";
import { EnvelopeIcon } from "../../../Icons/EnvelopeIcon";
import { ExitIcon } from "../../../Icons/ExitIcon";
import { LockIcon } from "../../../Icons/LockIcon";
import { ShieldIcon } from "../../../Icons/ShieldIcon";
import { TourIcon } from "../../../Icons/TourIcon";
import { UserIcon } from "../../../Icons/UserIcon";
import { getPatientAge } from "../../../../containers/Dashboard/Providers/schema/newPatientValidation";

import { MenuItem } from "./MenuItem";
import { MenuSection } from "./MenuSection";
import "./profile-menu-styles.scss";
import { cn } from "@/lib/helpers/cn";

export function PatientMenu(props) {
  const [popupWasOpened, setPopupWasOpened] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const isACPEligible = useSelector(selectIsACPEligible);
  const isTeensAndKidsProgram = useSelector(selectIsTeensAndKidsProgram);
  const { ref: popUpRef, isPopUpVisible, hidePopup, togglePopup } = usePopUpVisibility(false);

  const isMobile = window.innerWidth <= 640;
  const firstName = props?.profile?.preferred_name || props?.profile?.first_name;
  let name = props.profile ? firstName + " " + props.profile.last_name : "";

  const displayAddChild = () => {
    return 18 < getPatientAge(props.profile.dob);
  };

  const dismissMenu = () => hidePopup();

  const toggleProfileMenu = () => {
    if (isMobile) {
      history.push(props.baseRoute + "/settings");
      return;
    }

    togglePopup();
  };

  const handleProfile = () => {
    dismissMenu();
    history.push(props.baseRoute + "/profile");
  };

  const handlePassword = () => {
    dismissMenu();
    history.push(props.baseRoute + "/change-password");
  };

  const handleAddChild = () => {
    dismissMenu();
    history.push(props.baseRoute + "/teens-and-kids/program-details");
  };

  const handleNotification = () => {
    dismissMenu();
    history.push(props.baseRoute + "/patient-notifications");
  };

  const handleTutorials = () => {
    dismissMenu();
    history.push(props.baseRoute + "/tutorial");
  };

  const handleSystemCheck = () => {
    dismissMenu();
    history.push(props.baseRoute + "/system-check");
  };

  const handleInsurance = () => {
    dismissMenu();
    history.push(props.baseRoute + "/patient-insurance");
  };

  const handlePayment = () => {
    dismissMenu();
    history.push(props.baseRoute + "/patient-payment");
  };

  const handleLogout = () => {
    dismissMenu();
    dispatch(logOut({ history }));
  };

  const handleConsents = () => {
    dismissMenu();
    history.push(props.baseRoute + "/consents");
  };

  useEffect(() => {
    if (!popupWasOpened && isPopUpVisible) {
      setPopupWasOpened(true);
    }
  }, [isPopUpVisible]);

  const profileDropdown = (
    <div style={{ display: popupWasOpened ? "block" : "none" }}>
      <div
        className={cn(
          "Toolbar__ProfileMenu-dropdown tw:flex tw:flex-col tw:p-5 tw:gap-4",
          isPopUpVisible ? "menuOpen" : "menuClose",
          !isPopUpVisible && "disabled-dropdown",
        )}
      >
        <MenuSection title="ACCOUNT">
          <MenuItem icon={UserIcon} text="Profile" onClick={handleProfile} />

          <MenuItem
            icon={LockIcon}
            text="Password"
            onClick={handlePassword}
            automationDataAttr={getAutomationDataAttr("ProfileMenuChangePassword")}
          />

          <MenuItem
            icon={ShieldIcon}
            text="Insurance"
            onClick={handleInsurance}
            className="patient-dropdown-text" // CYPRESS TEST: MemberHeader.js:14 - DO NOT REMOVE
            {...getAutomationDataAttr("ProfileMenuInsuranceItem")}
          />

          {shouldShowVulnerablePages(props.profile.status) && (
            <MenuItem icon={CardIcon} text="Payment" onClick={handlePayment} />
          )}

          {displayAddChild() && (
            <MenuItem icon={AddUserIcon} text="Add Child" onClick={handleAddChild} />
          )}
          <MenuItem icon={BellIcon} text="Notifications" onClick={handleNotification} />
        </MenuSection>

        <MenuSection title="SUPPORT">
          <MenuItem
            icon={AptihealthIcon}
            text="About aptihealth"
            href="https://www.aptihealth.com/about/"
            target="_blank"
            rel="noreferrer"
          />

          <MenuItem
            icon={EnvelopeIcon}
            text="Member Support"
            href="mailto:support@aptihealth.com?subject=Web Support Request"
            target="_top"
          />

          <MenuItem icon={TourIcon} text="Tutorials" onClick={handleTutorials} />
          <MenuItem icon={CameraIcon} text="System & Video Check" onClick={handleSystemCheck} />
        </MenuSection>

        <MenuSection title="LEGAL">
          {isACPEligible && !isTeensAndKidsProgram && (
            <MenuItem
              icon={DocumentIcon}
              text="Consents"
              onClick={handleConsents}
              automationDataAttr={getAutomationDataAttr("profile-menu-consents")}
            />
          )}

          <MenuItem
            icon={DocumentIcon}
            text="HIPAA Notice"
            href="https://www.aptihealth.com/hipaaprivacy"
            target="_blank"
            rel="noreferrer"
          />
        </MenuSection>

        <MenuItem icon={ExitIcon} text="Log Out" onClick={handleLogout} />
      </div>
    </div>
  );

  const profileMenuItem = (
    <div
      onClick={toggleProfileMenu}
      className="Toolbar__ProfileMenu  cursor d-flex align-items-center"
    >
      <div
        className="Toolbar__ProfileMenu-name-wpr d-lg-flex flex-column d-md-none d-sm-none d-none align-content-end"
        style={{ marginTop: 0 }}
      >
        <span
          className="fs-16 txt-ellipsis NavUserNameSpanPatient" // CYPRESS TEST: MemberHeader.js:38-39 - DO NOT REMOVE
          style={{ whiteSpace: "nowrap" }}
        >
          {name}
        </span>
      </div>

      <div
        className="Toolbar__ProfileMenu-caret ml-2 d-lg-block d-md-none d-sm-none d-none" // CYPRESS TEST: MemberHeader.js:6 - DO NOT REMOVE
        {...getAutomationDataAttr("ProfileMenu")}
      >
        <ChevronDownIcon className="Toolbar__ChevronDown" iconSize={ICON_SIZES.SMALL} />
      </div>

      <div
        className="Toolbar__ProfileMenu-name-wpr d-block d-lg-none d-flex flex-column align-items-center"
        style={{ marginTop: 0, fill: "#FFFFFF" }}
      >
        <UserIcon iconType={ICON_TYPES.OUTLINE} iconSize={ICON_SIZES.SMALL} />
        <span className="ToolbarUserName fs-16 txt-ellipsis" style={{ whiteSpace: "nowrap" }}>
          {/* CYPRESS TEST: Used in mobile view - DO NOT REMOVE */}
          {name}
        </span>
      </div>
    </div>
  );

  return (
    <div ref={popUpRef} className="tw:relative">
      {props.profile ? profileMenuItem : <span className="Toolbar__ProfileMenu-placeholder" />}
      {profileDropdown}
    </div>
  );
}
